import { BOARD, CARDS_BY_ID, JAIL_FINE, MAX_JAIL_ATTEMPTS } from "@grundbesitz/shared";
import type { DebtReason, GameEvent, PaymentParty, Trade, TradeSide } from "@grundbesitz/shared";
import { fieldName, formatMoney } from "./format";
import type { SoundName } from "./sound";

/**
 * Turns engine events into German text for the log and toasts, and picks one sound per batch.
 * Pure functions: no store access, so they can be tested without a browser.
 */

export interface EventContext {
  /** The viewer's player id, null for spectators. */
  selfId: string | null;
  /** Display name of a player id. */
  playerName: (id: string) => string;
}

export type Tone = "info" | "good" | "bad" | "neutral";

export interface EventDescription {
  text: string;
  tone: Tone;
  involvesSelf: boolean;
}

const isPlayer = (p: PaymentParty | string): boolean => p !== "bank" && p !== "pot";

const DEBT_LABELS: Record<DebtReason, string> = {
  RENT: "Miete",
  TAX: "Steuer",
  CARD: "Kartenzahlung",
  BIRTHDAY: "Geburtstagsgeld",
  JAIL_FINE: "Kaution",
  MORTGAGE_INTEREST: "Hypothekenzinsen",
  REPAIRS: "Renovierungskosten",
};

function mortgageOf(field: number): number {
  const f = BOARD[field];
  return f && (f.kind === "STREET" || f.kind === "RAILROAD" || f.kind === "UTILITY") ? f.mortgage : 0;
}

function buildingLabel(buildings: number): string {
  if (buildings === 0) return "keine Gebäude";
  if (buildings === 5) return "ein Hotel";
  return buildings === 1 ? "1 Haus" : `${buildings} Häuser`;
}

/** Describes one event from the viewer's perspective. Returns null for events that add nothing to the log. */
export function describeEvent(event: GameEvent, ctx: EventContext): EventDescription | null {
  const self = (id: string): boolean => id === ctx.selfId;
  const name = (id: string): string => (self(id) ? "Du" : ctx.playerName(id));
  const dat = (id: string): string => (self(id) ? "dir" : ctx.playerName(id));
  const acc = (id: string): string => (self(id) ? "dich" : ctx.playerName(id));
  const verb = (id: string, third: string, second: string): string => (self(id) ? second : third);
  const money = formatMoney;

  const out = (text: string, tone: Tone, ...who: (string | null | undefined)[]): EventDescription => ({
    text,
    tone,
    involvesSelf: ctx.selfId !== null && who.some((w) => w === ctx.selfId),
  });

  const sideText = (side: TradeSide): string => {
    const parts: string[] = side.fields.map(fieldName);
    if (side.cash > 0) parts.push(money(side.cash));
    if (side.jailCards.length > 0) parts.push(side.jailCards.length === 1 ? "eine Freikarte" : `${side.jailCards.length} Freikarten`);
    return parts.length > 0 ? parts.join(", ") : "nichts";
  };
  const tradeText = (t: Trade): string => `${sideText(t.give)} gegen ${sideText(t.get)}`;
  const offerOf = (from: string): string => (self(from) ? "dein Angebot" : `das Angebot von ${ctx.playerName(from)}`);

  switch (event.type) {
    case "GAME_STARTED":
      return out(`Das Spiel beginnt. Reihenfolge: ${event.order.map(ctx.playerName).join(", ")}.`, "neutral", ...event.order);

    case "STREETS_DEALT": {
      const first = event.hands[0];
      const value = (first?.fields.reduce((sum, f) => sum + (BOARD[f] as { price: number }).price, 0) ?? 0) + (first?.compensation ?? 0);
      const evened = event.hands.filter((h) => h.compensation > 0).map((h) => `an ${acc(h.playerId)} ${money(h.compensation)}`);
      return out(
        `Alle Straßen sind verteilt, jeder bekommt Straßen im Wert von ${money(value)}.` +
          (evened.length > 0 ? ` Ausgleich von der Bank: ${evened.join(", ")}.` : ""),
        "info",
        ...event.hands.map((h) => h.playerId),
      );
    }

    case "START_ROLLS": {
      const rolls = event.rolls.map((r) => `${ctx.playerName(r.playerId)} ${r.dice[0] + r.dice[1]}`).join(", ");
      return out(
        event.round > 1 ? `Gleichstand, Nachwurf für den Start: ${rolls}` : `Startwurf: ${rolls}`,
        "neutral",
        ...event.rolls.map((r) => r.playerId),
      );
    }

    case "TURN_STARTED":
      return self(event.playerId)
        ? out("Du bist am Zug.", "info", event.playerId)
        : out(`${ctx.playerName(event.playerId)} ist am Zug.`, "neutral", event.playerId);

    case "TURN_ENDED":
      return null;

    case "DICE_ROLLED": {
      const [a, b] = event.dice;
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "würfelt", "würfelst")} ${a} und ${b}${event.doubles ? " (Pasch)" : ""}`,
        "neutral",
        event.playerId,
      );
    }

    case "PLAYER_MOVED": {
      if (event.reason === "JAIL") return null;
      const go = event.passedGo ? " und passiert LOS" : "";
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "zieht", "ziehst")} auf ${fieldName(event.to)}${go}`,
        "neutral",
        event.playerId,
      );
    }

    case "PAYMENT": {
      const { from, to, amount, reason, field } = event;
      const fname = field !== null ? fieldName(field) : "";
      switch (reason) {
        case "RENT":
          if (isPlayer(from) && isPlayer(to)) {
            return out(
              `${name(from)} ${verb(from, "zahlt", "zahlst")} ${dat(to)} ${money(amount)} Miete für ${fname}`,
              self(to) ? "good" : self(from) ? "bad" : "neutral",
              from,
              to,
            );
          }
          return null;
        case "TAX":
          return isPlayer(from)
            ? out(`${name(from)} ${verb(from, "zahlt", "zahlst")} ${money(amount)} ${fname}${to === "pot" ? " in den Frei-Parken-Topf" : ""}`, self(from) ? "bad" : "neutral", from)
            : null;
        case "CARD":
          return isPlayer(from)
            ? out(`${name(from)} ${verb(from, "zahlt", "zahlst")} ${money(amount)}${to === "pot" ? " in den Frei-Parken-Topf" : " an die Bank"}`, self(from) ? "bad" : "neutral", from)
            : null;
        case "REPAIRS":
          return isPlayer(from)
            ? out(`${name(from)} ${verb(from, "zahlt", "zahlst")} ${money(amount)} für Instandhaltung`, self(from) ? "bad" : "neutral", from)
            : null;
        case "BIRTHDAY":
          return isPlayer(from) && isPlayer(to)
            ? out(`${name(from)} ${verb(from, "zahlt", "zahlst")} ${dat(to)} ${money(amount)} zum Geburtstag`, self(to) ? "good" : self(from) ? "bad" : "neutral", from, to)
            : null;
        case "MORTGAGE_INTEREST":
          return isPlayer(from)
            ? out(`${name(from)} ${verb(from, "zahlt", "zahlst")} ${money(amount)} Hypothekenzinsen für ${fname}`, self(from) ? "bad" : "neutral", from)
            : null;
        case "GO":
          return isPlayer(to)
            ? out(`${name(to)} ${verb(to, "erhält", "erhältst")} ${money(amount)} für LOS`, self(to) ? "good" : "neutral", to)
            : null;
        case "GAIN":
          return isPlayer(to)
            ? out(`${name(to)} ${verb(to, "erhält", "erhältst")} ${money(amount)} von der Bank`, self(to) ? "good" : "neutral", to)
            : null;
        case "FREE_PARKING":
          return isPlayer(to)
            ? out(`${name(to)} ${verb(to, "erhält", "erhältst")} ${money(amount)} aus dem Frei-Parken-Topf`, self(to) ? "good" : "neutral", to)
            : null;
        // These payments are described by the event that caused them (purchase, build, trade ...).
        case "JAIL_FINE":
        case "PURCHASE":
        case "AUCTION":
        case "BUILD":
        case "SELL":
        case "MORTGAGE":
        case "UNMORTGAGE":
        case "TRADE":
        case "BANKRUPTCY":
          return null;
        default: {
          const unreachable: never = reason;
          return unreachable;
        }
      }
    }

    case "CARD_DRAWN": {
      const card = CARDS_BY_ID[event.cardId];
      const deck = event.deck === "chance" ? "eine Ereigniskarte" : "eine Gemeinschaftskarte";
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "zieht", "ziehst")} ${deck}${card ? `: „${card.text}“` : ""}`,
        "info",
        event.playerId,
      );
    }

    case "CARD_RETURNED":
      return null;

    case "PROPERTY_BOUGHT":
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "kauft", "kaufst")} ${fieldName(event.field)} für ${money(event.price)}`,
        self(event.playerId) ? "good" : "neutral",
        event.playerId,
      );

    case "PURCHASE_DECLINED":
      return out(`${name(event.playerId)} ${verb(event.playerId, "kauft", "kaufst")} ${fieldName(event.field)} nicht`, "neutral", event.playerId);

    case "RENT_DUE":
      if (self(event.owner)) {
        return out(`Du kannst ${money(event.amount)} Miete für ${fieldName(event.field)} von ${ctx.playerName(event.payer)} fordern`, "good", event.owner, event.payer);
      }
      if (self(event.payer)) {
        return out(`${ctx.playerName(event.owner)} kann ${money(event.amount)} Miete für ${fieldName(event.field)} von dir fordern`, "bad", event.owner, event.payer);
      }
      return out(`${ctx.playerName(event.owner)} kann ${money(event.amount)} Miete für ${fieldName(event.field)} von ${ctx.playerName(event.payer)} fordern`, "neutral", event.owner, event.payer);

    case "RENT_CLAIMED":
      return null;

    case "RENT_LAPSED":
      if (self(event.owner)) {
        return out(`Du hast die Miete von ${money(event.amount)} für ${fieldName(event.field)} nicht gefordert. Sie verfällt`, "bad", event.owner, event.payer);
      }
      return out(
        `${ctx.playerName(event.owner)} fordert die Miete von ${money(event.amount)} für ${fieldName(event.field)} nicht. Sie verfällt`,
        self(event.payer) ? "good" : "neutral",
        event.owner,
        event.payer,
      );

    case "SENT_TO_JAIL": {
      const why = event.reason === "TRIPLE_DOUBLES" ? "dritter Pasch" : event.reason === "CARD" ? "Karte" : "Feld „Gehen Sie in das Gefängnis“";
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "kommt", "kommst")} ins Gefängnis (${why})`,
        self(event.playerId) ? "bad" : "neutral",
        event.playerId,
      );
    }

    case "LEFT_JAIL": {
      const n = name(event.playerId);
      const leaves = "verlässt das Gefängnis";
      const v = (third: string, second: string) => verb(event.playerId, third, second);
      switch (event.how) {
        case "FINE":
          return out(`${n} ${v("zahlt", "zahlst")} ${money(JAIL_FINE)} Kaution und ${leaves}`, "neutral", event.playerId);
        case "CARD":
          return out(`${n} setzt eine Freikarte ein und ${leaves}`, "neutral", event.playerId);
        case "DOUBLES":
          return out(`${n} ${v("würfelt", "würfelst")} einen Pasch und ${leaves}`, "good", event.playerId);
        case "FORCED":
          return out(`${n} ${v("zahlt", "zahlst")} nach dem dritten Fehlversuch ${money(JAIL_FINE)} und ${leaves}`, "neutral", event.playerId);
        default: {
          const unreachable: never = event.how;
          return unreachable;
        }
      }
    }

    case "JAIL_ATTEMPT_FAILED":
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "würfelt", "würfelst")} keinen Pasch (Versuch ${event.attempt} von ${MAX_JAIL_ATTEMPTS})`,
        "neutral",
        event.playerId,
      );

    case "AUCTION_STARTED":
      return event.auction.kind === "PROPERTY" && event.auction.field !== null
        ? out(`Versteigerung: ${fieldName(event.auction.field)}`, "info")
        : out(`Versteigerung um ${event.auction.buildingKind === "hotel" ? "das letzte Hotel" : "das letzte Haus"}`, "info", ...event.auction.eligible);

    case "AUCTION_BID":
      return out(`${name(event.playerId)} ${verb(event.playerId, "bietet", "bietest")} ${money(event.amount)}`, "neutral", event.playerId);

    case "AUCTION_PASSED":
      return out(`${name(event.playerId)} ${verb(event.playerId, "steigt", "steigst")} aus`, "neutral", event.playerId);

    case "AUCTION_ENDED": {
      const what = event.kind === "PROPERTY" && event.field !== null ? fieldName(event.field) : "den Bauplatz";
      if (event.winnerId === null) {
        return event.kind === "PROPERTY" && event.field !== null
          ? out(`Niemand bietet: ${what} bleibt bei der Bank`, "neutral")
          : out("Niemand bietet: kein Gebäude wird gebaut", "neutral");
      }
      return out(
        `${name(event.winnerId)} ${verb(event.winnerId, "ersteigert", "ersteigerst")} ${what} für ${money(event.amount)}`,
        self(event.winnerId) ? "good" : "neutral",
        event.winnerId,
      );
    }

    case "BUILT":
      return out(
        event.buildings === 5
          ? `${name(event.playerId)} ${verb(event.playerId, "baut", "baust")} ein Hotel auf ${fieldName(event.field)}`
          : `${name(event.playerId)} ${verb(event.playerId, "baut", "baust")} ein Haus auf ${fieldName(event.field)} (${buildingLabel(event.buildings)})`,
        "neutral",
        event.playerId,
      );

    case "SOLD_BUILDING":
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "verkauft", "verkaufst")} ein Gebäude auf ${fieldName(event.field)} (${buildingLabel(event.buildings)})`,
        "neutral",
        event.playerId,
      );

    case "BUILD_WINDOW_OPENED":
      return out(`Der Vorrat wird knapp: Anmeldung für das letzte ${event.kind === "hotel" ? "Hotel" : "Haus"} läuft`, "info");

    case "BUILD_WINDOW_CLOSED":
      return null;

    case "MORTGAGED":
      return out(
        `${name(event.playerId)} ${verb(event.playerId, "nimmt", "nimmst")} eine Hypothek auf ${fieldName(event.field)} auf (${money(mortgageOf(event.field))})`,
        "neutral",
        event.playerId,
      );

    case "UNMORTGAGED":
      return out(`${name(event.playerId)} löst die Hypothek auf ${fieldName(event.field)} ab`, "neutral", event.playerId);

    case "PROPERTY_TRANSFERRED":
      return null;

    case "TRADE_PROPOSED": {
      const t = event.trade;
      if ((t.round ?? 0) > 0) {
        if (self(t.to)) return out(`${ctx.playerName(t.from)} macht dir ein Gegenangebot: ${tradeText(t)}`, "info", t.from, t.to);
        if (self(t.from)) return out(`Du machst ${ctx.playerName(t.to)} ein Gegenangebot: ${tradeText(t)}`, "neutral", t.from, t.to);
        return out(`${ctx.playerName(t.from)} macht ${ctx.playerName(t.to)} ein Gegenangebot: ${tradeText(t)}`, "neutral", t.from, t.to);
      }
      if (self(t.to)) return out(`${ctx.playerName(t.from)} bietet dir einen Handel an: ${tradeText(t)}`, "info", t.from, t.to);
      if (self(t.from)) return out(`Du bietest ${ctx.playerName(t.to)} einen Handel an: ${tradeText(t)}`, "neutral", t.from, t.to);
      return out(`${ctx.playerName(t.from)} bietet ${ctx.playerName(t.to)} einen Handel an: ${tradeText(t)}`, "neutral", t.from, t.to);
    }

    case "TRADE_ACCEPTED": {
      const t = event.trade;
      return out(
        `${name(t.to)} ${verb(t.to, "nimmt", "nimmst")} ${offerOf(t.from)} an: ${tradeText(t)}`,
        self(t.from) || self(t.to) ? "good" : "neutral",
        t.from,
        t.to,
      );
    }

    case "TRADE_REJECTED":
      return out(`${name(event.to)} ${verb(event.to, "lehnt", "lehnst")} ${offerOf(event.from)} ab`, "neutral", event.from, event.to);

    case "TRADE_CANCELLED":
      return out(
        `${name(event.from)} ${verb(event.from, "zieht", "ziehst")} ${self(event.from) ? "dein" : "das"} Angebot an ${acc(event.to)} zurück`,
        "neutral",
        event.from,
        event.to,
      );

    case "TRADE_INVALIDATED":
      return out(`${self(event.from) ? "Dein Angebot" : `Das Angebot von ${ctx.playerName(event.from)}`} an ${acc(event.to)} ist hinfällig`, "neutral", event.from, event.to);

    case "DEBT_CREATED": {
      const d = event.debt;
      const label = DEBT_LABELS[d.reason] + (d.reason === "RENT" && d.field !== null ? ` für ${fieldName(d.field)}` : "");
      const creditor = d.creditor === "bank" ? "der Bank" : dat(d.creditor);
      return out(
        `${self(d.debtor) ? "Du hast" : `${ctx.playerName(d.debtor)} hat`} Schulden bei ${creditor}: ${money(d.amount)} ${label}`,
        self(d.debtor) ? "bad" : "neutral",
        d.debtor,
        d.creditor === "bank" ? null : d.creditor,
      );
    }

    case "DEBT_SETTLED":
      return out(`${name(event.debtor)} ${verb(event.debtor, "begleicht", "begleichst")} die Schulden`, "neutral", event.debtor);

    case "BANKRUPT": {
      const n = name(event.playerId);
      if (event.resigned) {
        return out(`${n} ${verb(event.playerId, "gibt", "gibst")} auf und ${verb(event.playerId, "scheidet", "scheidest")} aus`, self(event.playerId) ? "bad" : "neutral", event.playerId);
      }
      const heir = event.creditor === "bank" ? "geht an die Bank" : `geht an ${acc(event.creditor)}`;
      return out(
        `${n} ${verb(event.playerId, "ist", "bist")} bankrott. Der Besitz ${heir}`,
        self(event.playerId) ? "bad" : "neutral",
        event.playerId,
        event.creditor === "bank" ? null : event.creditor,
      );
    }

    case "GAME_OVER": {
      const winner = event.winnerId;
      const tail = event.reason === "TIME_LIMIT" ? " Das Zeitlimit ist erreicht, es zählt das höchste Vermögen." : "";
      if (winner === null) return out(`Spiel beendet. Es gibt keinen Sieger.${tail}`, "info");
      return out(
        `Spiel beendet. Sieger: ${self(winner) ? "du" : ctx.playerName(winner)}.${tail}`,
        self(winner) ? "good" : "info",
        winner,
      );
    }

    default: {
      const unreachable: never = event;
      return unreachable;
    }
  }
}

/** Descriptions of a whole batch, in event order, without the events that add nothing. */
export function describeEvents(events: readonly GameEvent[], ctx: EventContext): EventDescription[] {
  const out: EventDescription[] = [];
  // A counter offer replaces the old offer. "Gegenangebot" already says it was not accepted.
  const countered = new Set(
    events.flatMap((e) => (e.type === "TRADE_PROPOSED" && (e.trade.round ?? 0) > 0 ? [`${e.trade.to}>${e.trade.from}`] : [])),
  );
  for (const e of events) {
    if (e.type === "TRADE_REJECTED" && countered.has(`${e.from}>${e.to}`)) continue;
    const d = describeEvent(e, ctx);
    if (d) out.push(d);
  }
  return out;
}

// ---------------------------------------------------------------- toasts

export interface ToastCandidate {
  text: string;
  kind: "info" | "success" | "error";
  priority: number;
}

/** A toast for events the player should not miss, null for the rest. */
export function toastFor(event: GameEvent, ctx: EventContext): ToastCandidate | null {
  const d = describeEvent(event, ctx);
  if (!d) return null;
  const kind: ToastCandidate["kind"] = d.tone === "good" ? "success" : d.tone === "bad" ? "error" : "info";
  const make = (priority: number): ToastCandidate => ({ text: d.text, kind, priority });
  switch (event.type) {
    case "PAYMENT":
      return event.reason === "RENT" && d.involvesSelf ? make(5) : null;
    case "RENT_DUE":
    case "DEBT_CREATED":
      return d.involvesSelf ? make(5) : null;
    case "RENT_LAPSED":
      return d.involvesSelf ? make(4) : null;
    case "PROPERTY_BOUGHT":
      return d.involvesSelf ? make(3) : null;
    case "AUCTION_ENDED":
      return event.winnerId !== null && d.involvesSelf ? make(3) : null;
    case "SENT_TO_JAIL":
      return make(4);
    case "TRADE_PROPOSED":
      return ctx.selfId !== null && event.trade.to === ctx.selfId ? make(6) : null;
    case "TRADE_ACCEPTED":
      return d.involvesSelf ? make(6) : null;
    case "BANKRUPT":
      return make(7);
    case "GAME_OVER":
      return make(8);
    default:
      return null;
  }
}

/** The most important toasts of a batch (at most `max`), in event order. */
export function pickToasts(events: readonly GameEvent[], ctx: EventContext, max = 2): ToastCandidate[] {
  const candidates = events
    .map((e, i) => ({ t: toastFor(e, ctx), i }))
    .filter((x): x is { t: ToastCandidate; i: number } => x.t !== null);
  const top = [...candidates].sort((a, b) => b.t.priority - a.t.priority || a.i - b.i).slice(0, max);
  return top.sort((a, b) => a.i - b.i).map((x) => x.t);
}

// ---------------------------------------------------------------- sounds

const CASH_REASONS: ReadonlySet<string> = new Set(["RENT", "TAX", "CARD", "BIRTHDAY", "JAIL_FINE", "MORTGAGE_INTEREST", "REPAIRS", "GO", "GAIN", "FREE_PARKING", "TRADE"]);

/** One sound for a whole batch, by priority, so many events never produce a wall of noise. */
export function soundFor(events: readonly GameEvent[], ctx: EventContext): SoundName | null {
  const has = (pred: (e: GameEvent) => boolean) => events.some(pred);
  const self = ctx.selfId;
  if (has((e) => e.type === "GAME_OVER")) return "win";
  if (has((e) => e.type === "BANKRUPT")) return "bankrupt";
  if (has((e) => e.type === "SENT_TO_JAIL")) return "jail";
  if (has((e) => e.type === "AUCTION_ENDED")) return "auction";
  if (has((e) => e.type === "PAYMENT" && CASH_REASONS.has(e.reason) && (e.from === self || e.to === self))) return "cash";
  if (has((e) => (e.type === "TRADE_PROPOSED" && e.trade.to === self) || (e.type === "TRADE_ACCEPTED" && (e.trade.from === self || e.trade.to === self)))) return "trade";
  if (has((e) => e.type === "BUILT")) return "build";
  if (has((e) => e.type === "PROPERTY_BOUGHT")) return "buy";
  if (has((e) => e.type === "CARD_DRAWN")) return "card";
  if (has((e) => e.type === "TURN_STARTED" && e.playerId === self)) return "turn";
  if (has((e) => e.type === "DICE_ROLLED" && e.doubles)) return "doubles";
  if (has((e) => e.type === "DICE_ROLLED")) return "dice";
  if (has((e) => e.type === "AUCTION_STARTED" || e.type === "AUCTION_BID")) return "bid";
  return null;
}
